/**
 * Bilingual copy for the Features page.
 * `en` holds the exact original English strings; `km` mirrors them in Khmer.
 */
export const en = {
  heroKicker: 'Features',
  title: 'What AKAS does for your business',
  lead:
    'Every part of AKAS is built to remove a daily headache — chasing approvals, compiling payroll, guessing at stock, waiting to get paid. Here is what each area does for your business.',
  userGuide: 'Guide ↗',
  cta: {
    title: 'Ready to see it with your own data?',
    description:
      'Try the live demos today, or tell us about your business and we will walk you through the parts that matter most to you.',
  },
  groups: [
    {
      id: 'workflows',
      kicker: 'Stop chasing signatures',
      title: 'Approvals that move at business speed',
      description:
        'Leave, purchases, quotes, and stock requests follow the approval path you choose — automatically. The right manager is notified, requests move in hours instead of weeks, and every decision is on record.',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🔀',
          title: 'Set the rules once, reuse them forever',
          body:
            'Point and click to define how each type of request flows: who receives it, what checks apply, who signs off, and who gets told the outcome. No IT project, no custom software — and a process that works for one department can be copied for the next.',
        },
        {
          icon: '✅',
          title: 'Nothing waits in an inbox',
          body:
            'Every request lands in the approver’s queue the moment it is submitted, with automatic reminders. Managers see exactly what needs their decision; owners can see where anything is stuck.',
        },
        {
          icon: '🤝',
          title: 'No bottleneck when someone is away',
          body:
            'Approving managers can hand their sign-off to a colleague for a few days or a holiday, then take it back. Operations keep moving without sharing passwords or risking an unapproved payment.',
        },
        {
          icon: '🗂️',
          title: 'A clean record for audits and disputes',
          body:
            'Every approval — who, when, and what changed — is stored with the request. When a customer, tax officer, or auditor asks, the answer is one click away.',
        },
      ],
    },
    {
      id: 'hr',
      kicker: 'People instead of paperwork',
      title: 'HR: hire, manage, and keep your best people',
      description:
        'One employee record follows each person from the first interview to the exit interview. HR stops re-keying forms and starts managing; employees self-serve the requests that used to eat their week.',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '💻',
          title: 'Employees help themselves',
          body:
            'Staff request leave, overtime, training, company stock, or a resignation from their own dashboard — no forms on paper, no HR as middleman. HR reviews exceptions instead of typing requests.',
        },
        {
          icon: '👥',
          title: 'Attract and screen candidates faster',
          body:
            'Post a vacancy, let candidates apply online, and keep every application, CV, and note in one place. CVs are read for you, so shortlisting takes minutes — and nothing good gets lost.',
        },
        {
          icon: '📄',
          title: 'The whole story in one file',
          body:
            'Contracts, education, experience, documents, salaries, and job history live on one record. When a promotion or transfer happens, the history is kept — you always know who did what, when, and on what pay.',
        },
        {
          icon: '📝',
          title: 'Contracts and letters in one click',
          body:
            'Generate employment contracts and standard letters to PDF or Word from your templates. Professional documents without the weekend of formatting.',
        },
      ],
    },
    {
      id: 'payroll',
      kicker: 'Payday without the fear',
      title: 'Payroll that follows Cambodian rules',
      description:
        'Salary tax, NSSF, seniority, provident and pension funds are calculated inside the payroll run — so month-end is a review, not a rescue, and ministry or bank reports come out ready to file.',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '🧮',
          title: 'Every earnings type you actually pay',
          body:
            'Monthly and hourly pay, overtime, bonuses and allowances, child-care contributions, and twice-monthly runs for businesses that pay mid-month and month-end — all configured to your policies.',
        },
        {
          icon: '📊',
          title: 'Reports the bank and ministries expect',
          body:
            'Produce the salary tax declaration (TOS01), NSSF contribution forms, fringe benefit tax, seniority pay, and pension reports — plus transfer lists your bank accepts — straight from the payroll run.',
        },
        {
          icon: '⏱️',
          title: 'Overtime without disputes',
          body:
            'Staff request overtime and their manager approves it before it reaches payroll. Approved hours flow into the run automatically — no blue books, no guesswork.',
        },
        {
          icon: '💳',
          title: 'Payslips staff can trust',
          body:
            'Every employee downloads their own payslip, in English or Khmer, any time. Fewer “where is my pay?” visits to the HR desk.',
        },
      ],
    },
    {
      id: 'attendance',
      kicker: 'Time you can trust',
      title: 'Attendance, leave & schedules',
      description:
        'Know who is at work, on time, and absence-free — whether staff clock in on a fingerprint scanner, a QR code at the job site, or a phone in the field.',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '📷',
          title: 'Clock-in that cannot be faked',
          body:
            'Fingerprint devices and scan-to-clock-in QR posters record real presence. Off-site staff log field visits with start and end times — you pay for hours actually worked.',
        },
        {
          icon: '⏰',
          title: 'Your lateness policy, applied fairly',
          body:
            'Set grace minutes, deductions, and absent-to-leave rules once. The system applies them consistently to everyone — no arguments on payday.',
        },
        {
          icon: '🌴',
          title: 'Leave balances with no surprises',
          body:
            'Annual, sick, maternity and other leaves accrue and reset automatically. Staff see their balance before they ask; managers approve against real numbers.',
        },
        {
          icon: '🎯',
          title: 'Grow people, not just files',
          body:
            'Run performance reviews that staff take part in, and let them request the training they want — so development is tracked, not improvised.',
        },
      ],
    },
    {
      id: 'sales',
      kicker: 'Win and collect',
      title: 'Sales: from first call to money in the bank',
      description:
        'Track prospects, win quotes, bill, and collect — on one connected pipeline. Sales sees the deal; finance sees the invoice; your cash position is never a guess.',
      docsUrl: 'https://docs.akas.co/en/crm/',
      items: [
        {
          icon: '📈',
          title: 'A sales forecast you can actually use',
          body:
            'Every opportunity carries its value, stage, and closing odds. The pipeline view shows where revenue sits, so weekly meetings coach real deals instead of reviewing last month.',
        },
        {
          icon: '🧾',
          title: 'Invoices customers pay faster',
          body:
            'Send the invoice as a secure web link. Your customer opens it on their phone, sees the document, and pays immediately with scan-to-pay (KHQR) — cash arrives earlier, and fewer invoices age.',
        },
        {
          icon: '💰',
          title: 'Deposits and credit under control',
          body:
            'Take deposits and apply them to invoices later. Payment terms work out due dates automatically, and a receivables report ages every bill so you know exactly who to call.',
        },
        {
          icon: '🧮',
          title: 'Tax paperwork that prepares itself',
          body:
            'Generate the VAT and withholding sale journals your accountant needs at filing time — no compiling spreadsheets at month-end.',
        },
        {
          icon: '🎟️',
          title: 'Promotions your marketing can run',
          body:
            'Launch discount coupons — percent or fixed, with minimum spend, caps, expiry date, and per-customer limits — then watch adoption in the sales reports. Wholesale and retail price lists apply automatically by customer.',
        },
      ],
    },
    {
      id: 'purchasing',
      kicker: 'Buy smarter',
      title: 'Buying, stock & suppliers',
      description:
        'One trail from purchase order to paid bill, across every warehouse and shop — so you know what you own, what you owe, and what is quietly disappearing.',
      docsUrl: 'https://docs.akas.co/en/expense/',
      items: [
        {
          icon: '🛒',
          title: 'The full buying trail',
          body:
            'Order it, receive it, bill it, pay it — every step is matched to the last. Finance and the warehouse always agree, so nothing is paid twice and nothing is delivered unrecorded.',
        },
        {
          icon: '🚚',
          title: 'Stock you can believe',
          body:
            'On-hand quantities update with every receipt, transfer, sale, and stock count, per warehouse. Shrinkage and stockouts surface early — not at the next physical count.',
        },
        {
          icon: '💸',
          title: 'Supplier money properly tracked',
          body:
            'Prepay a supplier with a deposit and apply it to later bills. A payables report shows what you owe and when it falls due — so you always know before you pay.',
        },
        {
          icon: '📋',
          title: 'Start from your spreadsheets',
          body:
            'Import vendors, items, customers, and opening balances from Excel templates. Day one is a working system, not a blank database.',
        },
      ],
    },
    {
      id: 'finance',
      kicker: 'Close the books, calmly',
      title: 'Accounting & assets',
      description:
        'Sales, purchases, and payroll post to the ledger as they are approved. Statements are ready when you want them, and depreciation is handled in the books — not in a side spreadsheet.',
      docsUrl: 'https://docs.akas.co/en/accounting/',
      items: [
        {
          icon: '📒',
          title: 'Journals write themselves',
          body:
            'When a document is approved, its accounting entries post automatically to the right accounts. Your accountant reviews real-time books instead of re-entering everyone else’s work.',
        },
        {
          icon: '📈',
          title: 'The numbers, whenever asked',
          body:
            'P&L, balance sheet, cash flow, and budget-versus-actual at a click — from the same ledger the business operates on. See which branch or product line is winning at a glance.',
        },
        {
          icon: '🏗️',
          title: 'Fixed assets without the workbook',
          body:
            'Register every asset with class, location, and supplier. Depreciation posts on schedule with a preview you approve — year-end depreciation is already in the books.',
        },
        {
          icon: '🗓️',
          title: 'Nothing lands in the wrong period',
          body:
            'Closed fiscal periods reject late postings automatically — statements stay trustworthy all year, not just at an audit.',
        },
      ],
    },
    {
      id: 'retail',
      kicker: 'Sell everywhere',
      title: 'Shop counters & online store',
      description:
        'The till, the table, and the web shop all sell the same products, prices, and stock. Your marketing pushes one catalog, and every channel feeds one set of books.',
      docsUrl: 'https://docs.akas.co/en/pos/',
      items: [
        {
          icon: '🛒',
          title: 'A checkout that keeps the queue moving',
          body:
            'Scan or search, apply a coupon, print, next customer — on Windows, Mac, or Android tablets. Cashiers learn it in an afternoon.',
        },
        {
          icon: '🍽️',
          title: 'Restaurants under control',
          body:
            'Design your floor plan with tables, take orders, and print to the kitchen. Reprints send only what is new, so the kitchen never cooks an order twice — and the bill is right at the table.',
        },
        {
          icon: '🏪',
          title: 'Your own web shop, live this week',
          body:
            'A branded storefront at yourbrand.akas.co, with your products, banners, promotion pages, and terms — plus social links so customers reach you where they already scroll.',
        },
        {
          icon: '💳',
          title: 'Payments Cambodians actually use',
          body:
            'Checkout accepts scan-to-pay (KHQR), bank card payments (ABA PayWay), and cash on delivery. Every transaction flows into the same revenue records your accountants already use.',
        },
      ],
    },
    {
      id: 'ai',
      kicker: 'Answers, not reports',
      title: 'Ask your business anything',
      description:
        'An assistant that knows your own numbers. Managers and staff simply ask — “what is my leave balance?”, “which invoices are unpaid?” — and get an answer without filing a report request.',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🤖',
          title: 'Plug in the AI you prefer',
          body:
            'Connect a global AI service, or run AI locally on your own hardware. You decide where your business data goes — useful even in privacy-sensitive industries.',
        },
        {
          icon: '💬',
          title: 'Self-serve information',
          body:
            'Staff get their own answers: balances, statuses, lists of unpaid invoices. Managers stop queuing for reports that the system already knows.',
        },
        {
          icon: '✂️',
          title: 'Time back on the routine work',
          body:
            'Reading candidate CVs, finding facts in records, drafting answers — the assistant does the first pass, your people make the decisions.',
        },
      ],
    },
    {
      id: 'platform',
      kicker: 'Built around your business',
      title: 'Security, control & your own branding',
      description:
        'Your company structure, security rules, languages, and document formats are set once and every department inherits them — and only the people who should see payroll see payroll.',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🏢',
          title: 'Structure that mirrors your business',
          body:
            'Companies, branches, departments, warehouses, and locations to any depth — with your logo, tax credentials, currencies, and fiscal calendar applied across every module.',
        },
        {
          icon: '🔐',
          title: 'The right people see the right things',
          body:
            'Staff sign in with their work account (Google or company directory included) and can add extra protection on sensitive accounts. Each role sees only its own menus; approving managers and record-keeping are separated for you.',
        },
        {
          icon: '🔌',
          title: 'Let big customers in, safely',
          body:
            'Give client companies their own portal sign-in to view and track their documents — professional service without email attachments or shared files.',
        },
        {
          icon: '🧩',
          title: 'Shapes to fit your business',
          body:
            'Rearrange screens and add the fields your industry needs — product expiry, project codes, client reference — with a drag-and-drop screen editor. Change next week without a software vendor.',
        },
        {
          icon: '📝',
          title: 'Numbers auditors love',
          body:
            'Invoices, receipts, and staff IDs follow your numbering scheme automatically. Sensitive-field changes record the before and after, and can be exported for a compliance review.',
        },
        {
          icon: '🌐',
          title: 'Khmer and English, everywhere',
          body:
            'Everyone works in the language they actually use — and product names, departments, and documents keep their correct Khmer names alongside the English ones.',
        },
      ],
    },
  ],
};

export type Copy = typeof en;

export const km: Copy = {
  heroKicker: 'មុខងារ',
  title: 'អ្វីដែល AKAS ធ្វើសម្រាប់អាជីវកម្មរបស់អ្នក',
  lead:
    'គ្រប់ផ្នែកនៃ AKAS ត្រូវបានបង្កើតឡើងដើម្បីដោះស្រាយបញ្ហាប្រចាំថ្ងៃ — ការតាមរកការអនុម័ត ការរៀបចំប្រាក់ខែ ការស្មានស្តុក និងការរង់ចាំការទូទាត់។ នេះជាអ្វីដែលផ្នែកនីមួយៗធ្វើសម្រាប់អាជីវកម្មរបស់អ្នក។',
  userGuide: 'មគ្គុទ្ទេសក៍ ↗',
  cta: {
    title: 'ចង់ឃើញវាជាមួយទិន្នន័យពិតរបស់អ្នកហើយឬនៅ?',
    description:
      'សាកល្បង Demo ផ្ទាល់ថ្ងៃនេះ ឬប្រាប់យើងអំពីអាជីវកម្មរបស់អ្នក ហើយយើងនឹងបង្ហាញផ្នែកដែលសំខាន់បំផុតសម្រាប់អ្នក។',
  },
  groups: [
    {
      id: 'workflows',
      kicker: 'ឈប់តាមរកហត្ថលេខា',
      title: 'ការអនុម័តដែលដើរលឿនតាមល្បឿនអាជីវកម្ម',
      description:
        'ការឈប់សម្រាក ការទិញ សម្រង់តម្លៃ និងសំណើស្តុក ធ្វើតាមផ្លូវអនុម័តដែលអ្នកជ្រើសរើស — ដោយស្វ័យប្រវត្តិ។ អ្នកគ្រប់គ្រងត្រឹមត្រូវទទួលបានការជូនដំណឹង សំណើដើរទៅមុខក្នុងរយៈពេលប៉ុន្មានម៉ោងជំនួសឱ្យសប្តាហ៍ ហើយការសម្រេចចិត្តទាំងអស់ត្រូវបានកត់ត្រាទុក។',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🔀',
          title: 'កំណត់ច្បាប់តែម្តង ប្រើបានរហូត',
          body:
            'ចង្អុលហើយចុចដើម្បីកំណត់របៀបដែលសំណើនីមួយៗដើរ៖ អ្នកណាទទួល ការពិនិត្យអ្វីដែលអនុវត្ត អ្នកណាអនុម័ត និងអ្នកណាដែលត្រូវបានប្រាប់ពីលទ្ធផល។ មិនត្រូវការគម្រោង IT ឬកម្មវិធីផ្ទាល់ខ្លួនទេ — ហើយដំណើរការដែលដំណើរការល្អសម្រាប់ផ្នែកមួយ អាចចម្លងប្រើសម្រាប់ផ្នែកបន្ទាប់បាន។',
        },
        {
          icon: '✅',
          title: 'គ្មានអ្វីរង់ចាំក្នុងប្រអប់សំបុត្រ',
          body:
            'គ្រប់សំណើទាំងអស់ទៅដល់ជួររង់ចាំរបស់អ្នកអនុម័តភ្លាមៗនៅពេលដាក់ស្នើ ជាមួយការជូនដំណឹងស្វ័យប្រវត្តិ។ អ្នកគ្រប់គ្រងឃើញច្បាស់ពីអ្វីដែលត្រូវការការសម្រេចចិត្តរបស់ខ្លួន ហើយម្ចាស់អាជីវកម្មអាចមើលឃើញថាតើអ្វីដែលគាំង នៅកន្លែងណា។',
        },
        {
          icon: '🤝',
          title: 'គ្មានការគាំងពេលមនុស្សណាមួយអវត្តមាន',
          body:
            'អ្នកគ្រប់គ្រងដែលមានសិទ្ធិអនុម័តអាចប្រគល់កិច្ចការចុះហត្ថលេខាឲ្យសហការីជាបណ្តោះអាសន្នមួយរយៈពីរបីថ្ងៃ ឬនៅថ្ងៃសម្រាក រួចទទួលយកវាមកវិញ។ ប្រតិបត្តិការដើរទៅមុខជាបន្តទៀត ដោយមិនចាំបាច់ចែករំលែកពាក្យសម្ងាត់ ហើយមិនមានហានិភ័យនៃការទូទាត់ដែលមិនទាន់អនុម័ត។',
        },
        {
          icon: '🗂️',
          title: 'កំណត់ត្រាស្អាតសម្រាប់សវាកម្ម និងករណីជម្លោះ',
          body:
            'ការអនុម័តទាំងអស់ — អ្នកណា ពេលណា និងអ្វីដែលបានផ្លាស់ប្តូរ — ត្រូវបានរក្សាទុកជាមួយសំណើ។ នៅពេលអតិថិជន មន្ត្រីពន្ធ ឬអ្នកសវនកម្មសួរ ចម្លើយគ្រាន់តែចុចតែមួយប៉ុណ្ណោះ។',
        },
      ],
    },
    {
      id: 'hr',
      kicker: 'មនុស្សជំនួសឱ្យក្រដាស',
      title: 'ធនធានមនុស្ស៖ ជ្រើសរើស គ្រប់គ្រង និងរក្សាបុគ្គលិកល្អបំផុតរបស់អ្នក',
      description:
        'កំណត់ត្រាបុគ្គលិកតែមួយតាមដានមនុស្សម្នាក់ៗ ចាប់ពីសម្ភាសន៍ដំបូងដល់សម្ភាសន៍ពេលចេញពីការងារ។ ធនធានមនុស្ស ឈប់បញ្ចូលទម្រង់ឡើងវិញ ហើយចាប់ផ្តើមធ្វើការគ្រប់គ្រងជាក់ស្តែង ខណៈបុគ្គលិកប្រើសេវាប្រើដោយខ្លួនឯងសម្រាប់សំណើដែលធ្លាប់ចំណាយពេលទាំងសប្តាហ៍របស់ពួកគេ។',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '💻',
          title: 'បុគ្គលិកជួយខ្លួនឯង',
          body:
            'បុគ្គលិកស្នើសុំការឈប់សម្រាក ម៉ោងបន្ថែម បណ្តុះបណ្តាល ការដកស្តុកក្រុមហ៊ុន ឬការលាលែងពីការងារពី dashboard របស់ខ្លួន — គ្មានទម្រង់ក្រដាស ហើយគ្មានធនធានមនុស្ស ឈរនៅចំកណ្តាលទេ។ ធនធានមនុស្ស ពិនិត្យតែករណីខុសប្រក្រតី ជំនួសឱ្យការបញ្ចូលសំណើទាំងអស់។',
        },
        {
          icon: '👥',
          title: 'ទាក់ទាញ និងដកស្រង់បេក្ខជនបានលឿនជាងមុន',
          body:
            'ចេញផ្សាយតំណែងទំនេរ ដោយឲ្យបេក្ខជនដាក់ពាក្យតាមអ៊ីនធឺណិត ហើយរក្សាទុកការសុំ ស៊ីវី (CV) និងកំណត់សម្គាល់ទាំងអស់នៅកន្លែងតែមួយ។ CV ត្រូវបានអានឲ្យអ្នករួច ដូច្នេះការដកស្រង់បេក្ខជនត្រឹមតែចំណាយពេលប៉ុន្មាននាទី — ហើយគ្មានបេក្ខជនល្អណាដែលត្រូវបាត់បង់។',
        },
        {
          icon: '📄',
          title: 'រឿងរ៉ាវទាំងមូលក្នុងឯកសារតែមួយ',
          body:
            'កិច្ចសន្យា ការសិក្សា បទពិសោធន៍ ឯកសារ ប្រាក់ខែ និងប្រវត្តិការងារទាំងអស់នៅលើកំណត់ត្រាតែមួយ។ នៅពេលមានការឡើងតំណែង ឬផ្ទេរការងារ ប្រវត្តិត្រូវបានរក្សាទុក — អ្នកដឹងជានិច្ចថាអ្នកណាធ្វើអ្វី ពេលណា និងជាមួយប្រាក់ខែប៉ុន្មាន។',
        },
        {
          icon: '📝',
          title: 'កិច្ចសន្យា និងសំបុត្រក្នុងការចុចតែមួយ',
          body:
            'បង្កើតកិច្ចសន្យាការងារ និងសំបុត្រស្តង់ដារជា PDF ឬ Word ពីទម្រង់របស់អ្នក។ ឯកសារវិជ្ជាជីវៈ ដោយមិនចាំបាច់ចំណាយថ្ងៃឈប់សម្រាក ដើម្បីតម្រៀបទម្រង់។',
        },
      ],
    },
    {
      id: 'payroll',
      kicker: 'ថ្ងៃបើកប្រាក់ខែដោយគ្មានកង្វល់',
      title: 'ការគណនាប្រាក់ខែដែលអនុវត្តតាមច្បាប់កម្ពុជា',
      description:
        'ពន្ធលើប្រាក់ខែ NSSF អាយុការ មូលនិធិសន្សំ និងមូលនិធិចូលនិវត្តន៍ ត្រូវបានគណនាក្នុងដំណើរការគណនាប្រាក់ខែ — ដូច្នេះចុងខែគ្រាន់តែជាការពិនិត្យ មិនមែនជាការសង្គ្រោះទេ ហើយរបាយការណ៍សម្រាប់ក្រសួង ឬធនាគារត្រូវបានរៀបចំរួចរាល់ដើម្បីដាក់ជូន។',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '🧮',
          title: 'ប្រភេទចំណូលគ្រប់ប្រភេទដែលអ្នកជាក់ស្តែងបង់',
          body:
            'ប្រាក់ខែប្រចាំខែ និងតាមម៉ោង ម៉ោងបន្ថែម ប្រាក់រង្វាន់ និងប្រាក់ឧបត្ថម្ភ ការចូលរួមថែទាំកុមារ និងការគណនាពីរដងក្នុងខែសម្រាប់អាជីវកម្មដែលបង់ពាក់កណ្តាលខែ និងចុងខែ — ទាំងអស់ត្រូវបានកំណត់តាមគោលការណ៍របស់អ្នក។',
        },
        {
          icon: '📊',
          title: 'របាយការណ៍ដែលធនាគារ និងក្រសួងរំពឹងទុក',
          body:
            'បង្កើតសេចក្តីប្រកាសពន្ធលើប្រាក់ខែ (TOS01) ទម្រង់ចូលរួម NSSF ពន្ធលើអត្ថប្រយោជន៍បន្ថែម ប្រាក់អាយុការ និងរបាយការណ៍មូលនិធិចូលនិវត្តន៍ — រួមទាំងបញ្ជីផ្ទេរប្រាក់ដែលធនាគារទទួលយកបាន — ចេញពីដំណើរការគណនាប្រាក់ខែផ្ទាល់។',
        },
        {
          icon: '⏱️',
          title: 'ម៉ោងបន្ថែមដោយគ្មានជម្លោះ',
          body:
            'បុគ្គលិកស្នើម៉ោងបន្ថែម ហើយអ្នកគ្រប់គ្រងអនុម័តមុនពេលដល់ការគណនាប្រាក់ខែ។ ម៉ោងដែលបានអនុម័តហូរចូលដំណើរការគណនាដោយស្វ័យប្រវត្តិ — គ្មានសៀវភៅកត់ដៃ គ្មានការស្មាន។',
        },
        {
          icon: '💳',
          title: 'ស្លឹកប្រាក់ខែដែលបុគ្គលិកជឿជាក់បាន',
          body:
            'បុគ្គលិកគ្រប់រូបទាញយកស្លឹកប្រាក់ខែផ្ទាល់ខ្លួន ជាភាសាខ្មែរ ឬអង់គ្លេស នៅពេលណាក៏បាន។ ការមកសួរតុធនធានមនុស្ស ថា «ប្រាក់ខែខ្ញុំនៅឯណា» ក៏កាន់តែតិចទៅៗ។',
        },
      ],
    },
    {
      id: 'attendance',
      kicker: 'ពេលវេលាដែលអ្នកជឿជាក់បាន',
      title: 'វត្តមាន ការឈប់សម្រាក និងកាលវិភាគ',
      description:
        'ដឹងថានរណាកំពុងធ្វើការ មកត្រូវពេល និងគ្មានការអវត្តមាន — មិនថាបុគ្គលិកចុះវត្តមានតាមម៉ាស៊ីនបោះម្រាមដៃ តាម QR code នៅទីតាំងធ្វើការ ឬតាមទូរស័ព្ទខណៈចេញការងារខាងក្រៅ។',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      items: [
        {
          icon: '📷',
          title: 'ការចុះវត្តមានដែលមិនអាចក្លែងក្លាយបាន',
          body:
            'ឧបករណ៍ស្កេនម្រាមដៃ និងផ្ទាំង QR សម្រាប់ស្កេនចុះវត្តមាន កត់ត្រាវត្តមានពិតប្រាកដ។ បុគ្គលិកធ្វើការខាងក្រៅកត់ត្រាបេសកកម្មជាមួយពេលចាប់ផ្តើម និងពេលបញ្ចប់ — អ្នកបង់តែម៉ោងដែលបានធ្វើការពិត។',
        },
        {
          icon: '⏰',
          title: 'គោលការណ៍ការមកយឺតរបស់អ្នក ដែលអនុវត្តដោយយុត្តិធម៌',
          body:
            'កំណត់នាទីដែលអាចអនុញ្ញាត ការដកប្រាក់ និងច្បាប់បំប្លែងការអវត្តមានទៅជាការឈប់សម្រាក តែម្តង។ ប្រព័ន្ធអនុវត្តទាំងនេះស្មើៗគ្នាដល់គ្រប់គ្នា — គ្មានជម្លោះនៅថ្ងៃបើកប្រាក់ខែ។',
        },
        {
          icon: '🌴',
          title: 'ចំនួនថ្ងៃច្បាប់នៅសល់ ដោយគ្មានការភ្ញាក់ផ្អើល',
          body:
            'ច្បាប់ប្រចាំឆ្នាំ ច្បាប់ឈឺ ច្បាប់ស្ត្រីសម្រាលកូន និងច្បាប់ផ្សេងៗសន្សំ និងកំណត់ឡើងវិញដោយស្វ័យប្រវត្តិ។ បុគ្គលិកឃើញចំនួននៅសល់របស់ខ្លួនមុនពេលស្នើសុំ ហើយអ្នកគ្រប់គ្រងអនុម័តដោយពឹងលើលេខពិត។',
        },
        {
          icon: '🎯',
          title: 'អភិវឌ្ឍមនុស្ស មិនត្រឹមតែឯកសារ',
          body:
            'រៀបចំការពិនិត្យលទ្ធផលការងារ (performance review) ដែលបុគ្គលិកចូលរួម ហើយអនុញ្ញាតឲ្យពួកគេស្នើសុំបណ្តុះបណ្តាលដែលពួកគេចង់បាន — ដូច្នេះការអភិវឌ្ឍត្រូវបានតាមដានជាប្រព័ន្ធ មិនមែនធ្វើតាមចិត្តរៀងៗខ្លួនទេ។',
        },
      ],
    },
    {
      id: 'sales',
      kicker: 'ឈ្នះ និងទទួលប្រាក់បាន',
      title: 'ការលក់៖ ចាប់ពីទូរស័ព្ទលើកដំបូងដល់ប្រាក់ចូលធនាគារ',
      description:
        'តាមដានអតិថិជនសក្តានុពល ឈ្នះសម្រង់តម្លៃ ចេញវិក្កយបត្រ និងទទួលប្រាក់ — លើ pipeline តែមួយដែលភ្ជាប់គ្នា។ ផ្នែកលក់ឃើញកិច្ចសន្យា ផ្នែកហិរញ្ញវត្ថុឃើញវិក្កយបត្រ ហើយស្ថានភាពសាច់ប្រាក់របស់អ្នកមិនដែលជាការស្មានទេ។',
      docsUrl: 'https://docs.akas.co/en/crm/',
      items: [
        {
          icon: '📈',
          title: 'ការព្យាករណ៍លក់ដែលអ្នកប្រើប្រាស់បានពិតៗ',
          body:
            'ឱកាសលក់នីមួយៗផ្ទុកតម្លៃ ដំណាក់កាល និងលទ្ធភាពបិទកិច្ចសន្យា។ ទិដ្ឋភាព pipeline បង្ហាញថាចំណូលស្ថិតនៅទីណា ដូច្នេះការប្រជុំប្រចាំសប្តាហ៍ផ្តោតលើកិច្ចសន្យាពិតៗ ជំនួសឱ្យការពិនិត្យខែមុនឡើងវិញ។',
        },
        {
          icon: '🧾',
          title: 'វិក្កយបត្រដែលអតិថិជនទូទាត់លឿន',
          body:
            'ផ្ញើវិក្កយបត្រជាតំណវេបសាយដែលមានសុវត្ថិភាព។ អតិថិជនរបស់អ្នកបើកវាលើទូរស័ព្ទ ឃើញឯកសារ ហើយទូទាត់ភ្លាមៗដោយការស្កេនទូទាត់ (KHQR) — សាច់ប្រាក់ទៅដល់មុនពេល ហើយវិក្កយបត្រត្រូវរង់ចាំយូរកាន់តែតិច។',
        },
        {
          icon: '💰',
          title: 'ប្រាក់កក់ និងឥណទានដែលគ្រប់គ្រងបាន',
          body:
            'ទទួលប្រាក់កក់ ហើយយកទៅកាត់លើវិក្កយបត្រនៅពេលក្រោយ។ លក្ខខណ្ឌទូទាត់គណនាថ្ងៃកំណត់ត្រូវបង់ដោយស្វ័យប្រវត្តិ ហើយរបាយការណ៍ឥណទានតាមដានអាយុវិក្កយបត្រនីមួយៗ ដូច្នេះអ្នកដឹងច្បាស់ថាត្រូវទូរស័ព្ទរកអ្នកណា។',
        },
        {
          icon: '🧮',
          title: 'ឯកសារពន្ធដែលរៀបចំដោយខ្លួនឯង',
          body:
            'បង្កើតបំរែបំរួលលក់ VAT និងពន្ធដកនៅប្រភព (withholding) ដែលគណនេយ្យកររបស់អ្នកត្រូវការនៅពេលប្រកាស — គ្មានការផ្គុំរួម spreadsheet នៅចុងខែទេ។',
        },
        {
          icon: '🎟️',
          title: 'សកម្មភាពផ្សព្វផ្សាយដែលទីផ្សាររបស់អ្នកអាចប្រតិបត្តិបាន',
          body:
            'ចេញកូប៉ុនបញ្ចុះតម្លៃ — ភាគរយ ឬចំនួនថេរ រួមជាមួយចំណាយអប្បបរមា ដែនកំណត់ ថ្ងៃផុតកំណត់ និងកំណត់សម្រាប់អតិថិជនម្នាក់ៗ — រួចតាមដានការប្រើប្រាស់ក្នុងរបាយការណ៍លក់។ តម្លៃលក់ដុំ និងតម្លៃលក់រាយអនុវត្តដោយស្វ័យប្រវត្តិតាមប្រភេទអតិថិជន។',
        },
      ],
    },
    {
      id: 'purchasing',
      kicker: 'ទិញឱ្យបានឆ្លាតវៃ',
      title: 'ការទិញ ស្តុក និងអ្នកផ្គត់ផ្គង់',
      description:
        'ដានតែមួយ ចាប់ពីបញ្ជាទិញរហូតដល់វិក្កយបត្រទូទាត់រួច ពេញគ្រប់ឃ្លាំង និងហាងទាំងអស់ — ដូច្នេះអ្នកដឹងថាអ្វីដែលអ្នកមាន អ្វីដែលអ្នកជំពាក់ និងអ្វីដែលកំពុងរសាត់បាត់ដោយស្ងាត់ៗ។',
      docsUrl: 'https://docs.akas.co/en/expense/',
      items: [
        {
          icon: '🛒',
          title: 'ដានការទិញពេញលេញ',
          body:
            'បញ្ជាទិញ ទទួល ចេញវិក្កយបត្រ និងទូទាត់ — ជំហាននីមួយៗភ្ជាប់ជាមួយជំហានមុន។ ផ្នែកហិរញ្ញវត្ថុ និងឃ្លាំងមានទិន្នន័យដូចគ្នាជានិច្ច ដូច្នេះគ្មានអ្វីត្រូវបង់ពីរដង ហើយគ្មានអ្វីដែលដឹកជញ្ជូនដល់ត្រូវបានរំលងកត់ត្រាទេ។',
        },
        {
          icon: '🚚',
          title: 'ស្តុកដែលអ្នកជឿជាក់បាន',
          body:
            'បរិមាណស្តុកក្នុងដៃធ្វើបច្ចុប្បន្នភាពជាមួយការទទួល ការផ្ទេរ ការលក់ និងការរាប់ស្តុក តាមឃ្លាំងនីមួយៗ។ ការបាត់បង់ និងស្តុកអស់លេចឡើងឱ្យឃើញមុន — មិនមែនរង់ចាំដល់ការរាប់ស្តុកផ្ទាល់លើកក្រោយទេ។',
        },
        {
          icon: '💸',
          title: 'ប្រាក់របស់អ្នកផ្គត់ផ្គង់តាមដានត្រឹមត្រូវ',
          body:
            'បង់ប្រាក់កក់ជាមុនដល់អ្នកផ្គត់ផ្គង់ ហើយយកទៅកាត់លើវិក្កយបត្រក្រោយៗ។ របាយការណ៍ឥណទានបង្ហាញថាអ្នកជំពាក់អ្វី និងថ្ងៃណាត្រូវបង់ — ដូច្នេះអ្នកដឹងជានិច្ចមុនពេលបង់ប្រាក់។',
        },
        {
          icon: '📋',
          title: 'ចាប់ផ្តើមពី spreadsheet របស់អ្នក',
          body:
            'នាំចូលអ្នកផ្គត់ផ្គង់ ទំនិញ អតិថិជន និងសមតុល្យបើកគណនី ចេញពីទម្រង់ Excel។ ថ្ងៃទីមួយក្លាយជាប្រព័ន្ធដំណើរការជាក់ស្តែង មិនមែនមូលដ្ឋានទិន្នន័យទំនេរទេ។',
        },
      ],
    },
    {
      id: 'finance',
      kicker: 'បិទសៀវភៅគណនេយ្យ ដោយស្ងប់ស្ងាត់',
      title: 'គណនេយ្យ និងទ្រព្យសកម្ម',
      description:
        'ការលក់ ការទិញ និងប្រាក់ខែចុះបញ្ជីនៅពេលបានអនុម័ត។ របាយការណ៍ហិរញ្ញវត្ថុរួចរាល់នៅពេលអ្នកចង់បាន ហើយការរំលស់ត្រូវបានគ្រប់គ្រងក្នុងសៀវភៅគណនេយ្យ — មិនមែននៅក្នុង spreadsheet ដាច់ដោយឡែកទេ។',
      docsUrl: 'https://docs.akas.co/en/accounting/',
      items: [
        {
          icon: '📒',
          title: 'បំរែបំរួលគណនី (journal) សរសេរខ្លួនឯង',
          body:
            'នៅពេលឯកសារត្រូវបានអនុម័ត កំណត់ត្រាគណនេយ្យរបស់វាចុះបញ្ជីទៅគណនីត្រឹមត្រូវដោយស្វ័យប្រវត្តិ។ គណនេយ្យកររបស់អ្នកពិនិត្យសៀវភៅពេលជាក់ស្តែង ជំនួសឱ្យការបញ្ចូលឡើងវិញនូវការងាររបស់អ្នកដទៃ។',
        },
        {
          icon: '📈',
          title: 'តួលេខទាំងនោះ នៅពេលណាដែលសូមបាន',
          body:
            'របាយការណ៍ចំណេញ-ចំណាយ (P&L) តុល្យភាព លំហូរសាច់ប្រាក់ និងថវិកាប្រៀបធៀបនឹងការអនុវត្តជាក់ស្តែង នៅគ្រាន់តែចុចម្តង — ចេញពីបញ្ជីតែមួយដែលអាជីវកម្មដំណើរការលើវា។ ឃើញភ្លាមៗថាសាខា ឬបន្ទាត់ផលិតផលណាមួយដែលឈ្នះ។',
        },
        {
          icon: '🏗️',
          title: 'ទ្រព្យសកម្មជាក់លាក់ ដោយគ្មាន workbook',
          body:
            'ចុះឈ្មោះទ្រព្យសកម្មនីមួយៗជាមួយប្រភេទ ទីតាំង និងអ្នកផ្គត់ផ្គង់។ ការរំលស់ចុះបញ្ជីតាមកាលវិភាគ ជាមួយសេចក្តីព្រាងដែលអ្នកអនុម័ត — ការរំលស់ចុងឆ្នាំស្ថិតនៅក្នុងសៀវភៅរួចហើយ។',
        },
        {
          icon: '🗓️',
          title: 'គ្មានអ្វីធ្លាក់ចូលរយៈពេលខុស',
          body:
            'រយៈពេលគណនេយ្យដែលបានបិទ បដិសេធការចុះបញ្ជីយឺតដោយស្វ័យប្រវត្តិ — របាយការណ៍ហិរញ្ញវត្ថុនៅជឿជាក់បានពេញមួយឆ្នាំ មិនត្រឹមតែពេលសវនកម្មទេ។',
        },
      ],
    },
    {
      id: 'retail',
      kicker: 'លក់គ្រប់ទីកន្លែង',
      title: 'គ្រឿងគិតលុយ និងហាងអនឡាញ',
      description:
        'គ្រឿងគិតលុយ តុភោជនីយដ្ឋាន និងហាងអនឡាញ លក់ផលិតផល តម្លៃ និងស្តុកដូចគ្នា។ ទីផ្សាររបស់អ្នកប្រើកាតាឡុកតែមួយ ហើយគ្រប់ឆានែលចូលទៅសៀវភៅគណនេយ្យតែមួយ។',
      docsUrl: 'https://docs.akas.co/en/pos/',
      items: [
        {
          icon: '🛒',
          title: 'ការគិតលុយដែលធ្វើឱ្យជួរដើរបន្តទៅមុខ',
          body:
            'ស្កេន ឬស្វែងរក អនុវត្តកូប៉ុន (coupon) បោះពុម្ព អតិថិជនបន្ទាប់ — លើ Windows, Mac ឬថេបឡេត Android។ អ្នកគិតលុយរៀនវាបានក្នុងរយៈពេលរសៀលមួយ។',
        },
        {
          icon: '🍽️',
          title: 'ភោជនីយដ្ឋានដែលគ្រប់គ្រងបាន',
          body:
            'រចនាប្លង់តុរបស់អ្នក ទទួលការកម្មង់ និងបោះពុម្ពផ្ទាល់ទៅផ្នែកម្ហូប។ ការបោះពុម្ពឡើងវិញផ្ញើតែអ្វីដែលថ្មី ដូច្នេះផ្នែកម្ហូបមិនដែលចម្អិនការកម្មង់ដូចគ្នាពីរដង — ហើយវិក្កយបត្រនៅតុជាក់លាក់របស់អតិថិជនត្រឹមត្រូវជាក់លាក់។',
        },
        {
          icon: '🏪',
          title: 'ហាងតាមអ៊ីនធឺណិតផ្ទាល់ខ្លួន បើកបានក្នុងសប្តាហ៍នេះ',
          body:
            'ហាងលក់ដែលមានម៉ាកសកម្មភាពនៅ yourbrand.akas.co ជាមួយផលិតផល បដា ទំព័រផ្សព្វផ្សាយ និងលក្ខខណ្ឌរបស់អ្នក — រួមទាំងតំណបណ្តាញសង្គម ដើម្បីឲ្យអតិថិជនទាក់ទងអ្នកនៅលើបណ្តាញដែលពួកគេប្រើប្រាស់រួចហើយ។',
        },
        {
          icon: '💳',
          title: 'ការទូទាត់ដែលខ្មែរប្រើពិតៗ',
          body:
            'ការគិតលុយទទួលការទូទាត់តាមការស្កេន (KHQR) ការទូទាត់តាមកាតធនាគារ (ABA PayWay) និងទូទាត់ពេលដឹកទំនិញដល់។ រាល់ប្រតិបត្តិការហូរចូលកំណត់ត្រាចំណូលដែលគណនេយ្យកររបស់អ្នកប្រើប្រាស់រួចហើយ។',
        },
      ],
    },
    {
      id: 'ai',
      kicker: 'ចម្លើយ មិនមែនរបាយការណ៍',
      title: 'សួរអាជីវកម្មរបស់អ្នកអ្វីក៏បាន',
      description:
        'ជំនួយករដែលស្គាល់លេខផ្ទាល់ខ្លួនរបស់អ្នក។ អ្នកគ្រប់គ្រង និងបុគ្គលិកគ្រាន់តែសួរ — «ចំនួនថ្ងៃច្បាប់របស់ខ្ញុំនៅសល់ប៉ុន្មាន?» «វិក្កយបត្រណាដែលមិនទាន់ទូទាត់?» — ហើយទទួលបានចម្លើយ ដោយមិនចាំបាច់ដាក់សំណើរបាយការណ៍។',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🤖',
          title: 'ភ្ជាប់ AI ដែលអ្នកចូលចិត្ត',
          body:
            'ភ្ជាប់ជាមួយសេវាកម្ម AI អន្តរជាតិ ឬដំណើរការ AI នៅលើឧបករណ៍ផ្ទាល់ខ្លួនរបស់អ្នក។ អ្នកជាអ្នកសម្រេចថាទិន្នន័យអាជីវកម្មរបស់អ្នកទៅទីណា — មានប្រយោជន៍សូម្បីតែក្នុងឧស្សាហកម្មដែលប្រយ័ត្ននឹងភាពឯកជន។',
        },
        {
          icon: '💬',
          title: 'ព័ត៌មានសេវាប្រើដោយខ្លួនឯង',
          body:
            'បុគ្គលិកទទួលបានចម្លើយផ្ទាល់ខ្លួន៖ សមតុល្យ ស្ថានភាព និងបញ្ជីវិក្កយបត្រដែលមិនទាន់ទូទាត់។ អ្នកគ្រប់គ្រងឈប់ឈររង់ចាំរបាយការណ៍ដែលប្រព័ន្ធដឹងរួចហើយ។',
        },
        {
          icon: '✂️',
          title: 'សន្សំពេលវេលាពីការងារប្រចាំថ្ងៃ',
          body:
            'អាន CV បេក្ខជន ស្វែងរកព័ត៌មានក្នុងកំណត់ត្រា និងជាព្រាងចម្លើយ — ជំនួយករធ្វើជំហានដំបូង ហើយមនុស្សរបស់អ្នកជាអ្នកសម្រេចចិត្ត។',
        },
      ],
    },
    {
      id: 'platform',
      kicker: 'សាងសង់ឡើងជុំវិញអាជីវកម្មរបស់អ្នក',
      title: 'សុវត្ថិភាព ការគ្រប់គ្រង និងម៉ាកផ្ទាល់ខ្លួនរបស់អ្នក',
      description:
        'រចនាសម្ព័ន្ធក្រុមហ៊ុន ច្បាប់សុវត្ថិភាព ភាសា និងទម្រង់ឯកសាររបស់អ្នកកំណត់តែម្តង ហើយគ្រប់ផ្នែកទាំងអស់ទទួលមរតកវា — ហើយមានតែមនុស្សដែលគួរឃើញប្រាក់ខែប៉ុណ្ណោះដែលឃើញប្រាក់ខែ។',
      docsUrl: 'https://docs.akas.co/en/admin/',
      items: [
        {
          icon: '🏢',
          title: 'រចនាសម្ព័ន្ធដែលឆ្លុះបញ្ចាំងអាជីវកម្មរបស់អ្នក',
          body:
            'ក្រុមហ៊ុន សាខា នាយកដ្ឋាន ឃ្លាំង និងទីតាំងច្រើនជាន់តាមតែចង់បាន — ជាមួយ logo ព័ត៌មានបញ្ជាក់ពន្ធដារ រូបិយប័ណ្ណ និងប្រតិទិនហិរញ្ញវត្ថុរបស់អ្នក អនុវត្តជាទូទៅលើគ្រប់ម៉ូឌុល។',
        },
        {
          icon: '🔐',
          title: 'មនុស្សត្រឹមត្រូវឃើញអ្វីត្រឹមត្រូវ',
          body:
            'បុគ្គលិកចូលប្រើដោយគណនីធ្វើការរបស់ខ្លួន (រួមទាំង Google ឬសៀវភៅឈ្មោះក្រុមហ៊ុន) ហើយអាចបន្ថែមការការពារបន្ថែមលើគណនីរសើប។ តួនាទីនីមួយៗឃើញតែម៉ឺនុយរបស់ខ្លួនប៉ុណ្ណោះ ហើយអ្នកអនុម័ត និងអ្នករក្សាកំណត់ត្រា ត្រូវបានបែងចែកដាច់ពីគ្នារួចហើយ។',
        },
        {
          icon: '🔌',
          title: 'ទទួលអតិថិជនធំ ដោយសុវត្ថិភាព',
          body:
            'ផ្តល់ឲ្យក្រុមហ៊ុនអតិថិជនចូលប្រើ portal ផ្ទាល់ខ្លួនរបស់ពួកគេ ដើម្បីមើល និងតាមដានឯកសាររបស់ពួកគេ — សេវាកម្មវិជ្ជាជីវៈ ដោយមិនចាំបាច់ភ្ជាប់ឯកសារតាមអ៊ីមែល ឬចែករំលែកឯកសារទាំងអស់ទេ។',
        },
        {
          icon: '🧩',
          title: 'រចនាឲ្យត្រូវនឹងអាជីវកម្មរបស់អ្នក',
          body:
            'រៀបចំអេក្រង់ឡើងវិញ និងបន្ថែមវាលដែលឧស្សាហកម្មរបស់អ្នកត្រូវការ — កាលបរិច្ឆេទផុតកំណត់ផលិតផល កូដគម្រោង និងលេខយោងអតិថិជន — ជាមួយកម្មវិធីកែអេក្រង់អូស-ដាក់។ ផ្លាស់ប្តូរនៅសប្តាហ៍ក្រោយដោយមិនចាំបាច់រង់ចាំអ្នកផ្គត់ផ្គង់កម្មវិធីទេ។',
        },
        {
          icon: '📝',
          title: 'លេខដែលអ្នកសវនកម្មចូលចិត្ត',
          body:
            'វិក្កយបត្រ បង្កាន់ដៃ និងលេខសម្គាល់បុគ្គលិក អនុវត្តតាមរបៀបកំណត់លេខរបស់អ្នកដោយស្វ័យប្រវត្តិ។ ការផ្លាស់ប្តូរលើវាលរសើបត្រូវកត់ត្រាពីមុន និងក្រោយ ហើយអាចនាំចេញសម្រាប់ការត្រួតពិនិត្យការអនុលោម។',
        },
        {
          icon: '🌐',
          title: 'ខ្មែរ និងអង់គ្លេស គ្រប់ទីកន្លែង',
          body:
            'អ្នកទាំងអស់ធ្វើការជាភាសាដែលពួកគេប្រើជាក់ស្តែង — ហើយឈ្មោះផលិតផល នាយកដ្ឋាន និងឯកសាររក្សាឈ្មោះខ្មែរដែលត្រឹមត្រូវជាមួយឈ្មោះអង់គ្លេស។',
        },
      ],
    },
  ],
};
